import { SUPPORTED_LOCALES, isLocale, type Locale } from '@unibyte/contracts/locales';
import { t, type WidgetKey } from '@unibyte/i18n';
import { safeAccent } from '../contrast.js';
import { onEscape, trapFocus } from '../keyboard.js';
import { readParentHandoff, type PublicWidgetConfig } from '../protocol.js';
import { safeLink } from '../safe-url.js';
import { resolveInitialLocale } from './locale.js';
import './styles.css';

const params = new URLSearchParams(window.location.search);
const parentOrigin = params.get('parent');
const apiUrl = __UNIBYTE_API_URL__.replace(/\/$/, '');

let locale: Locale = resolveInitialLocale(
  params.get('lang'),
  navigator.languages ?? [navigator.language],
);
let mode: 'auto' | 'manual' = 'auto';
let selected: Locale | null = null;
let token: string | null = null;
let conversationId: string | null = null;
let sessionGeneration = 0;
let config: PublicWidgetConfig | null = null;
let contactOpen = false;
let contactMessage = '';
let busy = false;
const root = document.getElementById('unibyte-chat-root');
if (!root) throw new Error('Missing widget root');

type Turn = {
  role: 'user' | 'assistant';
  text: string;
  messageId?: string;
  citations?: Array<{ label: string; url: string | null }>;
};

const turns: Turn[] = [];
let statusKey: WidgetKey = 'status.connecting';
let reconnectAttempts = 0;
let reconnectTimer: number | null = null;
let waiting = false;
let releaseFocusTrap: (() => void) | null = null;

function chromeLocale(): Locale {
  if (mode === 'manual' && selected) return selected;
  const enabled = config?.locales.map((entry) => entry.locale) ?? SUPPORTED_LOCALES;
  return enabled.includes(locale) ? locale : (config?.defaultLocale ?? locale);
}

function appendNoticeLink(notice: HTMLElement, href: string | null, label: string): void {
  if (!href) return;
  notice.append(
    document.createTextNode(' '),
    el('a', { href, target: '_blank', rel: 'noopener noreferrer' }, label),
  );
}

function render(): void {
  releaseFocusTrap?.();
  releaseFocusTrap = null;
  const current = chromeLocale();
  document.documentElement.lang = current;
  document.title = config?.title || t(current, 'header.title');
  document.documentElement.style.setProperty('--uc-accent', safeAccent(config?.accent));
  root!.replaceChildren();

  const app = el('div', { class: 'uc-app' });
  const header = el('header', { class: 'uc-header' });
  const brand = el('div', { class: 'uc-brand' });
  const title = config?.title || t(current, 'header.title');
  const mark =
    config?.logoUrl != null
      ? el('img', {
          class: 'uc-logo',
          src: config.logoUrl,
          alt: '',
          width: '28',
          height: '28',
        })
      : el('span', { class: 'uc-mark', 'aria-hidden': 'true' }, title.slice(0, 1).toUpperCase());
  const brandCopy = el('div', { class: 'uc-brand-copy' });
  const availability = el('p', { class: 'uc-availability' });
  availability.append(
    el('span', { class: 'uc-status-dot', 'aria-hidden': 'true' }),
    document.createTextNode(t(current, 'header.available')),
  );
  brandCopy.append(el('h1', {}, title), availability);
  brand.append(mark, brandCopy);
  header.append(brand);
  const tools = el('div', { class: 'uc-tools' });
  const langGroup = el('label', {
    class: 'uc-lang',
  });
  langGroup.append(
    el('span', { class: 'uc-visually-hidden' }, t(current, 'header.languageSelector')),
  );
  const langSelect = el('select', {
    id: 'language-select',
    'aria-label': t(current, 'header.languageSelector'),
  });
  const choices: Array<Locale | 'auto'> = [
    'auto',
    ...(config ? config.locales.map((entry) => entry.locale) : [...SUPPORTED_LOCALES]),
  ];
  for (const candidate of choices) {
    const active =
      candidate === 'auto' ? mode === 'auto' : mode === 'manual' && selected === candidate;
    langSelect.append(
      el(
        'option',
        {
          value: candidate,
          ...(active ? { selected: '' } : {}),
          ...(candidate === 'auto' ? {} : { lang: candidate }),
        },
        candidate === 'auto' ? t(current, 'language.auto') : candidate.toUpperCase(),
      ),
    );
  }
  langSelect.addEventListener('change', () => {
    const candidate = langSelect.value;
    if (candidate === 'auto') {
      mode = 'auto';
      selected = null;
    } else if (isLocale(candidate)) {
      mode = 'manual';
      selected = candidate;
      locale = candidate;
    }
    render();
  });
  langGroup.append(langSelect);
  const contactLabel = t(current, 'contact.open');
  const contact = el('button', {
    type: 'button',
    id: 'contact-open',
    class: 'uc-contact',
    'aria-label': contactLabel,
    title: contactLabel,
  });
  contact.append(contactIcon());
  contact.addEventListener('click', () => {
    contactMessage = '';
    contactOpen = true;
    render();
  });
  tools.append(langGroup, contact);
  const fresh = el(
    'button',
    {
      type: 'button',
      id: 'new-conversation',
      class: 'uc-icon-button',
      'aria-label': t(current, 'conversation.newConversation'),
      title: t(current, 'conversation.newConversation'),
    },
    '+',
  );
  fresh.addEventListener('click', () => {
    void startConversation(true);
  });
  header.append(tools, fresh);
  if (parentOrigin && window.parent !== window) {
    const close = el(
      'button',
      {
        type: 'button',
        id: 'widget-close',
        class: 'uc-icon-button',
        'aria-label': t(current, 'launcher.close'),
      },
      '×',
    );
    close.addEventListener('click', () => requestClose());
    header.append(close);
  }
  app.append(header);

  const notice = el('p', { class: 'uc-notice', role: 'note' });
  notice.append(el('span', {}, t(current, 'header.aiNoticeStrong')));
  appendNoticeLink(notice, safeLink(config?.privacyUrl), t(current, 'header.privacyLink'));
  appendNoticeLink(notice, safeLink(config?.contactUrl), t(current, 'header.contactLink'));
  app.append(notice);

  const offline = statusKey.startsWith('error.');
  if (offline && !waiting) {
    const banner = el('div', { class: 'uc-offline', role: 'alert' });
    banner.append(el('p', {}, t(current, statusKey)));
    const retry = el('button', { type: 'button' }, t(current, 'error.offline.retry'));
    retry.addEventListener('click', () => {
      reconnectAttempts = 0;
      stopReconnect();
      requestReconnect();
    });
    banner.append(retry);
    app.append(banner);
  }

  const messages = el('div', {
    class: 'uc-messages',
    role: 'log',
    'aria-live': 'polite',
    'aria-relevant': 'additions text',
    'aria-busy': String(busy),
    tabindex: '0',
  });
  if (turns.length === 0) {
    const localeConfig = config?.locales.find((entry) => entry.locale === current);
    const greeting = localeConfig?.greeting;
    if (offline) {
      messages.append(el('p', { class: 'uc-error', role: 'status' }, t(current, statusKey)));
    } else if (greeting) {
      const row = el('article', { class: 'uc-assistant' });
      row.append(el('p', {}, greeting));
      messages.append(assistantRow(row));
    } else {
      messages.append(el('p', { class: 'uc-status', role: 'status' }, t(current, statusKey)));
    }
    if (!offline && token && localeConfig?.starterQuestions.length) {
      const starters = el('div', {
        class: 'uc-starters',
        role: 'group',
        'aria-label': t(current, 'conversation.starterQuestions'),
      });
      for (const starter of localeConfig.starterQuestions) {
        const button = el('button', { type: 'button', class: 'uc-starter' });
        button.append(
          starterIcon(starter.icon),
          el('span', { class: 'uc-starter-label' }, starter.question),
          el('span', { class: 'uc-starter-arrow', 'aria-hidden': 'true' }, '→'),
        );
        button.addEventListener('click', () => {
          if (starter.action === 'CONTACT' || starter.action === 'QUOTE') {
            contactMessage = starter.action === 'QUOTE' ? t(current, 'contact.quoteMessage') : '';
            contactOpen = true;
            render();
            return;
          }
          turns.push({ role: 'user', text: starter.question });
          turns.push({ role: 'assistant', text: starter.answer });
          render();
        });
        starters.append(button);
      }
      messages.append(starters);
    }
  }
  for (const turn of turns) {
    const row = el('article', { class: turn.role === 'user' ? 'uc-user' : 'uc-assistant' });
    row.append(el('p', {}, turn.text));
    if (turn.citations && turn.citations.length > 0) {
      const block = el('div', { class: 'uc-sources-block' });
      const caption = el('p', { class: 'uc-sources-title' }, t(current, 'conversation.sources'));
      const hint = el('p', { class: 'uc-sources-hint' }, t(current, 'conversation.sourcesHint'));
      const sources = el('ul', { class: 'uc-sources' });
      for (const citation of turn.citations) {
        const item = el('li', {});
        const href = safeLink(citation.url);
        if (href)
          item.append(
            el('a', { href, target: '_blank', rel: 'noopener noreferrer' }, citation.label),
          );
        else item.textContent = citation.label;
        sources.append(item);
      }
      block.append(caption, hint, sources);
      row.append(block);
    }
    if (
      turn.role === 'assistant' &&
      turn.messageId &&
      token &&
      conversationId &&
      config?.showAnswerFeedback !== false
    ) {
      const actions = el('div', { class: 'uc-feedback' });
      for (const rating of ['HELPFUL', 'NOT_HELPFUL'] as const) {
        const key =
          rating === 'HELPFUL' ? 'conversation.feedbackHelpful' : 'conversation.feedbackNotHelpful';
        const button = el('button', { type: 'button' }, t(current, key));
        const messageId = turn.messageId;
        button.addEventListener('click', () => {
          void sendFeedback(messageId, rating, button);
        });
        actions.append(button);
      }
      row.append(actions);
    }
    messages.append(turn.role === 'assistant' ? assistantRow(row) : row);
  }
  if (waiting) {
    messages.append(
      el('p', { class: 'uc-waiting', role: 'status' }, t(current, 'status.reconnecting')),
    );
  } else if (busy) {
    messages.append(el('p', { class: 'uc-status' }, t(current, 'composer.sending')));
  }
  app.append(messages);

  const composer = el('form', { class: 'uc-composer' });
  const input = el('textarea', {
    id: 'composer',
    rows: '1',
    maxlength: '2000',
    placeholder: t(current, 'composer.placeholder'),
    'aria-label': t(current, 'composer.placeholder'),
  });
  if (!token || busy) input.disabled = true;
  const send = el('button', {
    type: 'submit',
    id: 'send',
    'aria-label': t(current, 'composer.send'),
  });
  send.append(sendIcon());
  if (!token || busy) send.disabled = true;
  composer.append(input, send);
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    if (!send.disabled) composer.requestSubmit(send);
  });
  composer.addEventListener('submit', (event) => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    void sendMessage(text, crypto.randomUUID());
  });
  app.append(composer);

  if (contactOpen) {
    const dialog = contactDialog(current);
    app.append(dialog);
    releaseFocusTrap = trapFocus(dialog);
  }
  root!.append(app);
  messages.scrollTop = messages.scrollHeight;
  if (contactOpen) document.getElementById('contact-name')?.focus();
  else if (token && !busy) focusComposer();
}

/** Full re-renders replace the textarea; put focus back when the visitor can type again. */
function focusComposer(): void {
  requestAnimationFrame(() => {
    const composer = document.getElementById('composer');
    if (composer instanceof HTMLTextAreaElement && !composer.disabled) composer.focus();
  });
}

function contactDialog(current: Locale): HTMLElement {
  const dialog = el('form', {
    class: 'uc-dialog',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'contact-title',
  });
  dialog.append(el('h2', { id: 'contact-title' }, t(current, 'contact.title')));
  dialog.append(el('p', {}, t(current, 'contact.notice')));
  const name = field('contact-name', t(current, 'contact.name'), 'text', true);
  const email = field('contact-email', t(current, 'contact.email'), 'email', true);
  const phone = field('contact-phone', t(current, 'contact.phone'), 'tel', false);
  const message = el('textarea', { id: 'contact-message', required: '', maxlength: '4000' });
  message.value = contactMessage;
  const messageLabel = el('label', {}, t(current, 'contact.message'));
  messageLabel.append(message);
  const notice = el('label', { class: 'uc-check' });
  const noticeInput = el('input', { id: 'contact-notice', type: 'checkbox', required: '' });
  notice.append(noticeInput, document.createTextNode(` ${t(current, 'contact.notice')}`));
  const marketing = el('label', { class: 'uc-check' });
  const marketingInput = el('input', { id: 'contact-marketing', type: 'checkbox' });
  marketing.append(
    marketingInput,
    document.createTextNode(` ${t(current, 'contact.marketingOptIn')}`),
  );
  const error = el('p', { id: 'contact-error', class: 'uc-error' });
  const actions = el('div', { class: 'uc-dialog-actions' });
  const cancel = el('button', { type: 'button' }, t(current, 'contact.close'));
  cancel.addEventListener('click', () => {
    contactOpen = false;
    contactMessage = '';
    render();
  });
  const submit = el('button', { type: 'submit' }, t(current, 'contact.submit'));
  actions.append(cancel, submit);
  dialog.append(name, email, phone, messageLabel, notice, marketing, error, actions);
  dialog.addEventListener('submit', (event) => {
    event.preventDefault();
    const nameValue = valueOf('contact-name');
    const emailValue = valueOf('contact-email');
    const messageValue = valueOf('contact-message');
    const accepted =
      (document.getElementById('contact-notice') as HTMLInputElement | null)?.checked ?? false;
    if (!nameValue || !emailValue || !messageValue || !accepted) {
      error.textContent = t(current, 'contact.required');
      return;
    }
    void sendLead(
      {
        name: nameValue,
        email: emailValue,
        phone: valueOf('contact-phone'),
        message: messageValue,
        marketingOptIn:
          (document.getElementById('contact-marketing') as HTMLInputElement | null)?.checked ??
          false,
        noticeAccepted: true,
      },
      error,
      current,
    );
  });
  return dialog;
}

async function sendLead(
  body: {
    name: string;
    email: string;
    phone: string;
    message: string;
    marketingOptIn: boolean;
    noticeAccepted: true;
  },
  error: HTMLElement,
  current: Locale,
): Promise<void> {
  if (!token || !conversationId) {
    error.textContent = t(current, 'error.offline');
    return;
  }
  try {
    const response = await post(`/v1/widget/conversations/${conversationId}/leads`, body, token);
    if (response.status === 201) {
      contactOpen = false;
      contactMessage = '';
      turns.push({ role: 'assistant', text: t(current, 'contact.success') });
      render();
      return;
    }
    error.textContent = t(current, errorKey(response.status, response.body));
  } catch (reason) {
    error.textContent = t(current, reason === 'timeout' ? 'error.timeout' : 'error.offline');
  }
}

function stopReconnect(): void {
  if (reconnectTimer == null) return;
  window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

/** Asks the host page for a fresh session. A proxy restart only lasts a few seconds. */
function requestReconnect(): void {
  if (reconnectTimer != null) return;
  if (!parentOrigin || window.parent === window || reconnectAttempts >= 6) {
    waiting = false;
    statusKey = 'error.offline';
    render();
    return;
  }
  const wait = Math.min(8_000, 500 * 2 ** reconnectAttempts);
  reconnectAttempts += 1;
  waiting = true;
  statusKey = 'status.reconnecting';
  render();
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    try {
      window.parent.postMessage({ type: 'unibyte-chat:reconnect' }, new URL(parentOrigin).origin);
    } catch {
      waiting = false;
      statusKey = 'error.offline';
      render();
    }
  }, wait);
}

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Repeats a call while the edge proxy is restarting. 4xx is returned immediately. */
async function postUntilBack(
  path: string,
  body: unknown,
  bearer?: string,
): Promise<{ status: number; body: unknown }> {
  const started = Date.now();
  let attempt = 0;
  let lastStatus = 0;
  const budget = () => (lastStatus === 409 ? 45_000 : 20_000);
  while (Date.now() - started < budget()) {
    try {
      const response = await post(path, body, bearer);
      if (response.status < 500 && response.status !== 409) {
        waiting = false;
        return response;
      }
      lastStatus = response.status;
      waiting = true;
      render();
    } catch (reason) {
      if (reason !== 'offline' && reason !== 'timeout') throw reason;
      waiting = true;
      render();
    }
    attempt += 1;
    const remaining = budget() - (Date.now() - started);
    if (remaining <= 0) break;
    await delay(Math.min(remaining, Math.min(4_000, 400 * 2 ** attempt)));
  }
  waiting = false;
  if (lastStatus > 0) return { status: lastStatus, body: null };
  throw 'offline';
}

async function sendMessage(text: string, requestId: string): Promise<void> {
  const generation = sessionGeneration;
  const activeToken = token;
  const activeConversation = conversationId;
  if (!activeToken || !activeConversation || busy) return;
  busy = true;
  const existing = turns.findIndex((turn) => turn.role === 'user' && turn.text === text);
  if (existing === -1) turns.push({ role: 'user', text });
  render();
  try {
    const response = await postUntilBack(
      `/v1/widget/conversations/${activeConversation}/messages`,
      { requestId, text, localeMode: mode, selectedLocale: selected },
      activeToken,
    );
    if (generation !== sessionGeneration) return;
    const body = response.body as {
      messageId?: unknown;
      locale?: unknown;
      reason?: unknown;
      answer?: unknown;
      citations?: unknown;
    } | null;
    if (response.status === 200 && body) {
      if (isLocale(body.locale)) locale = body.locale;
      const messageId = typeof body.messageId === 'string' ? body.messageId : undefined;
      const answer = typeof body.answer === 'string' ? body.answer.trim() : '';
      const answerText = answer
        ? answer
        : t(
            chromeLocale(),
            body.reason === 'unsupported_language'
              ? 'error.unsupportedLanguage'
              : 'conversation.noEvidence',
          );
      const citations = Array.isArray(body.citations)
        ? body.citations.flatMap((item) => {
            if (!item || typeof item !== 'object') return [];
            const label = (item as { label?: unknown }).label;
            const url = (item as { url?: unknown }).url;
            if (typeof label !== 'string') return [];
            return [{ label, url: typeof url === 'string' ? url : null }];
          })
        : [];
      const previous = turns.find((turn) => turn.messageId === messageId);
      if (!previous)
        turns.push({
          role: 'assistant',
          text: answerText,
          ...(messageId ? { messageId } : {}),
          ...(citations.length > 0 ? { citations } : {}),
        });
      statusKey = 'status.connecting';
    } else {
      turns.push({
        role: 'assistant',
        text: t(chromeLocale(), errorKey(response.status, response.body)),
      });
    }
  } catch (reason) {
    if (generation !== sessionGeneration) return;
    turns.push({
      role: 'assistant',
      text: t(chromeLocale(), reason === 'timeout' ? 'error.timeout' : 'error.offline'),
    });
  } finally {
    if (generation === sessionGeneration) {
      busy = false;
      render();
    }
  }
}

async function sendFeedback(
  messageId: string,
  rating: 'HELPFUL' | 'NOT_HELPFUL',
  button: HTMLButtonElement,
): Promise<void> {
  if (!token || !conversationId) return;
  button.disabled = true;
  try {
    const response = await post(
      `/v1/widget/conversations/${conversationId}/feedback`,
      { messageId, rating },
      token,
    );
    if (response.status === 201 || response.status === 409)
      button.textContent = t(chromeLocale(), 'conversation.feedbackThanks');
  } catch {
    button.disabled = false;
  }
}

async function startConversation(reset: boolean): Promise<void> {
  const generation = sessionGeneration;
  const activeToken = token;
  if (!activeToken) return;
  if (reset) {
    turns.splice(0, turns.length);
    conversationId = null;
  }
  const chosen = mode === 'manual' && selected ? selected : chromeLocale();
  try {
    const response = await postUntilBack(
      '/v1/widget/conversations',
      { locale: chosen },
      activeToken,
    );
    if (generation !== sessionGeneration) return;
    const id =
      response.body && typeof response.body === 'object'
        ? (response.body as { id?: unknown }).id
        : null;
    if (response.status === 201 && typeof id === 'string') {
      conversationId = id;
      statusKey = 'status.connecting';
    } else {
      statusKey = errorKey(response.status, response.body);
    }
  } catch (reason) {
    if (generation !== sessionGeneration) return;
    statusKey = reason === 'timeout' ? 'error.timeout' : 'error.offline';
  }
  render();
}

async function exchange(ticket: string, remote: PublicWidgetConfig): Promise<void> {
  const generation = ++sessionGeneration;
  config = remote;
  token = null;
  conversationId = null;
  turns.splice(0, turns.length);
  busy = false;
  statusKey = 'status.connecting';
  render();
  try {
    const response = await postWithChallenge('/v1/widget/sessions', { ticket });
    if (generation !== sessionGeneration) return;
    const sessionToken =
      response.body && typeof response.body === 'object'
        ? (response.body as { token?: unknown }).token
        : null;
    if (response.status !== 201 || typeof sessionToken !== 'string') {
      token = null;
      if (response.status >= 500) requestReconnect();
      else {
        statusKey = errorKey(response.status, response.body);
        render();
      }
      return;
    }
    token = sessionToken;
    reconnectAttempts = 0;
    waiting = false;
    stopReconnect();
    await startConversation(true);
  } catch (reason) {
    if (generation !== sessionGeneration) return;
    token = null;
    if (reason === 'timeout' || reason === 'offline') requestReconnect();
    else {
      statusKey = 'error.offline';
      render();
    }
  }
}

function errorKey(status: number, body: unknown): WidgetKey {
  const code =
    body && typeof body === 'object' ? (body as { error?: { code?: unknown } }).error?.code : null;
  if (code === 'QUOTA_EXCEEDED') return 'error.quotaExceeded';
  if (status === 429 || code === 'RATE_LIMITED') return 'error.rateLimited';
  if (status === 503) return 'error.offline';
  return 'error.generic';
}

async function post(
  path: string,
  body: unknown,
  bearer?: string,
): Promise<{ status: number; body: unknown }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), config?.httpTimeoutMs ?? 20_000);
  try {
    const response = await fetch(`${apiUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const payload: unknown = await response.json().catch(() => null);
    return { status: response.status, body: payload };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw 'timeout';
    throw 'offline';
  } finally {
    clearTimeout(timer);
  }
}

async function postWithChallenge(
  path: string,
  body: Record<string, unknown>,
): Promise<{ status: number; body: unknown }> {
  const { readChallenge, solveChallenge } = await import('../challenge.js');
  let payload = { ...body };
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await post(path, payload);
    if (response.status !== 429) return response;
    const challenge = readChallenge(response.body);
    if (!challenge || attempt === 1) return response;
    const solution = await solveChallenge(challenge.nonce, challenge.difficulty);
    payload = { ...body, challenge: { ...challenge, solution } };
  }
  return { status: 429, body: null };
}

function requestClose(): void {
  if (!parentOrigin || window.parent === window) return;
  try {
    window.parent.postMessage({ type: 'unibyte-chat:close' }, new URL(parentOrigin).origin);
  } catch {
    // Invalid parent origin stays silent.
  }
}

function assistantRow(bubble: HTMLElement): HTMLElement {
  const row = el('div', { class: 'uc-assistant-row' });
  row.append(
    el('p', { class: 'uc-role-label' }, t(chromeLocale(), 'conversation.aiAgent')),
    bubble,
  );
  return row;
}

function starterIcon(
  icon: 'QUESTION' | 'INFO' | 'SERVICES' | 'PRICE' | 'CLOCK' | 'CONTACT',
): SVGElement {
  const paths: Record<typeof icon, string> = {
    QUESTION:
      'M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.35-1 .8-1 1.7M12 17h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
    INFO: 'M12 11v6M12 7h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
    SERVICES: 'M4 7h16v12H4zM8 7V5h8v2M4 11h16M10 11v2h4v-2',
    PRICE: 'M12 3l7 7-9 9-7-7V3h9zM7.5 7.5h.01',
    CLOCK: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2',
    CONTACT: 'M4 6h16v12H4zM4 7l8 6 8-6',
  };
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'uc-starter-icon');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', paths[icon]);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.7');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);
  return svg;
}

function contactIcon(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.75');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('d', 'M4 7h16v11H4zM4 7l8 6 8-6');
  svg.append(path);
  return svg;
}

function sendIcon(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'uc-send-icon');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M5 12h14M13 6l6 6-6 6');
  svg.append(path);
  return svg;
}

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const next = onEscape({ widgetOpen: true, contactOpen });
  if (next.focus === 'contact') {
    contactOpen = false;
    contactMessage = '';
    render();
  }
  if (next.focus === 'launcher') requestClose();
});

window.addEventListener('message', (event: MessageEvent<unknown>) => {
  const handoff = readParentHandoff({
    origin: event.origin,
    source: event.source,
    parent: window.parent,
    expectedOrigin: parentOrigin,
    data: event.data,
  });
  if (!handoff) return;
  if (handoff.type === 'unibyte-chat:visible') {
    if (!token) {
      reconnectAttempts = 0;
      stopReconnect();
      requestReconnect();
    }
    return;
  }
  if (handoff.type === 'unibyte-chat:unavailable') {
    requestReconnect();
    return;
  }
  stopReconnect();
  void exchange(handoff.ticket, handoff.config);
});

render();

if (parentOrigin && window.parent !== window) {
  try {
    window.parent.postMessage({ type: 'unibyte-chat:ready' }, new URL(parentOrigin).origin);
  } catch {
    // Invalid parent origin stays silent.
  }
}

function field(id: string, label: string, type: string, required: boolean): HTMLLabelElement {
  const wrap = el('label', {}, label);
  const input = el('input', { id, type, ...(required ? { required: '' } : {}) });
  wrap.append(input);
  return wrap;
}

function valueOf(id: string): string {
  const node = document.getElementById(id);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Record<string, string> = {},
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
  if (text !== undefined) node.textContent = text;
  return node;
}
